import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { z } from "zod";
import {
  Activity,
  CalendarDays,
  ChevronRight,
  HeartPulse,
  Inbox,
  Pill,
  ScanHeart,
  Wind,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { normalizeUrgency } from "@/lib/urgency";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardHeader } from "@/components/ui/Card";
import { Button, LinkButton } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { StatTile } from "@/components/ui/StatTile";
import { Alert, EmptyState, LoadingPanel, UrgencyPanel } from "@/components/ui/Feedback";

const dashboardSchema = z.object({
  patient: z.object({
    name: z.string(),
    rm_number: z.string(),
  }),
  latest_check: z
    .object({
      session_uuid: z.string(),
      urgency: z.string(),
      summary: z.string().nullable().default(null),
      completed_at: z.string(),
    })
    .nullable()
    .default(null),
  vitals: z
    .array(
      z.object({
        metric: z.string(),
        label: z.string(),
        value: z.string(),
        unit: z.string().nullable().default(null),
        measured_at: z.string(),
      }),
    )
    .default([]),
  appointments: z
    .array(
      z.object({
        ticket: z.string(),
        doctor: z.string(),
        specialty: z.string(),
        date: z.string(),
        queue_number: z.string().nullable().default(null),
        status: z.string(),
      }),
    )
    .default([]),
  medications: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        dose: z.string().nullable().default(null),
        schedule_time: z.string(),
        taken_today: z.boolean().default(false),
      }),
    )
    .default([]),
});

const METRIC_ICONS: Record<string, typeof HeartPulse> = {
  heart_rate: HeartPulse,
  respiratory_rate: Wind,
  systolic_bp: Activity,
  spo2: Wind,
};

export function PatientDashboard() {
  const { data, isPending, error } = useQuery({
    queryKey: ["patient", "dashboard"],
    queryFn: ({ signal }) => api.get("/api/patient/dashboard", dashboardSchema, signal),
  });

  if (isPending) {
    return (
      <>
        <PageHeader title="Beranda" />
        <Card>
          <LoadingPanel label="Memuat ringkasan kesehatan" />
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Beranda" />
        <Alert tone="danger" title="Gagal memuat data">
          {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
        </Alert>
      </>
    );
  }

  const urgency = data.latest_check ? normalizeUrgency(data.latest_check.urgency) : null;

  return (
    <>
      <PageHeader
        eyebrow={`Rekam medis ${data.patient.rm_number}`}
        title={`Halo, ${data.patient.name.split(" ")[0]}`}
        description="Ringkasan kondisi, janji temu, dan pengingat obat Anda."
        actions={
          <LinkButton
            to="/patient/health-check"
            variant="primary"
            icon={<ScanHeart className="size-4" />}
          >
            Cek kesehatan
          </LinkButton>
        }
      />

      {/* The most recent clinical result leads the page — it is the one thing a
          patient opens this screen to see. */}
      {urgency && data.latest_check && (
        <UrgencyPanel
          level={urgency}
          title={data.latest_check.summary ?? undefined}
          className="mb-6"
        >
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="text-xs opacity-80">
              Pemeriksaan terakhir {formatDate(data.latest_check.completed_at)}
            </span>
            <Link
              to={`/patient/health-check/result/${data.latest_check.session_uuid}`}
              className="inline-flex items-center gap-0.5 text-xs font-bold underline underline-offset-2"
            >
              Lihat detail
              <ChevronRight className="size-3.5" />
            </Link>
          </div>
        </UrgencyPanel>
      )}

      {data.vitals.length > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {data.vitals.map((vital) => {
            const Icon = METRIC_ICONS[vital.metric] ?? Activity;
            return (
              <StatTile
                key={vital.metric}
                label={vital.label}
                value={vital.value}
                unit={vital.unit ?? undefined}
                caption={formatDate(vital.measured_at)}
                icon={<Icon className="size-4" />}
              />
            );
          })}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <Card>
          <CardHeader
            eyebrow="Janji temu"
            title="Jadwal mendatang"
            action={
              <LinkButton
                to="/patient/reservasi"
                size="sm"
                variant="ghost"
                icon={<CalendarDays className="size-4" />}
              >
                Buat baru
              </LinkButton>
            }
          />
          {data.appointments.length === 0 ? (
            <EmptyState
              icon={<CalendarDays className="size-5" />}
              title="Belum ada janji temu"
              description="Reservasi yang Anda buat akan tampil di sini."
            />
          ) : (
            <ul className="divide-hairline divide-y">
              {data.appointments.map((appointment) => (
                <li key={appointment.ticket} className="flex items-center gap-3 px-5 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{appointment.doctor}</p>
                    <p className="text-content-muted truncate text-xs">
                      {appointment.specialty} · {formatDate(appointment.date)}
                    </p>
                  </div>
                  {appointment.queue_number && (
                    <span className="tabular bg-surface-sunken rounded-lg px-2 py-1 text-xs font-bold">
                      {appointment.queue_number}
                    </span>
                  )}
                  <Badge tone={appointment.status === "confirmed" ? "routine" : "neutral"}>
                    {appointment.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader eyebrow="Pengingat" title="Obat hari ini" />
          {data.medications.length === 0 ? (
            <EmptyState
              icon={<Pill className="size-5" />}
              title="Tidak ada pengingat obat"
              description="Tambahkan pengingat agar tidak terlewat."
            />
          ) : (
            <ul className="divide-hairline divide-y">
              {data.medications.map((medication) => (
                <li key={medication.id} className="flex items-center gap-3 px-5 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{medication.name}</p>
                    <p className="text-content-muted truncate text-xs">
                      {[medication.dose, medication.schedule_time].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {medication.taken_today ? (
                    <Badge tone="routine" dot>
                      Sudah diminum
                    </Badge>
                  ) : (
                    <Button size="sm">Tandai</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {!data.latest_check && data.vitals.length === 0 && (
        <Card className="mt-4">
          <EmptyState
            icon={<Inbox className="size-5" />}
            title="Belum ada data kesehatan"
            description="Mulai pemeriksaan pertama untuk melihat tren dan riwayat Anda di sini."
            action={
              <LinkButton
                to="/patient/health-check"
                variant="primary"
                size="sm"
                icon={<ScanHeart className="size-4" />}
              >
                Mulai pemeriksaan
              </LinkButton>
            }
          />
        </Card>
      )}
    </>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}
